import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Sheet } from '@mui/joy';
import axios from 'axios';
import domain from "../../../util/domain";
import { useNavigate } from 'react-router-dom';

const formatPhoneId = (id) => {
    const numStr = id.toString();
    return numStr.startsWith('91') ? numStr.slice(2) : numStr;
};

const Chat = () => {
    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchMessages();
        // Set up polling for new messages every 5 seconds
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, [page]);

    const fetchMessages = async () => {
        try {
            const response = await axios.get(`${domain}/whatsapp/chat/messages`, {
                params: {
                    page: page,
                    limit: 50
                }
            });

            // Handle new paginated response format
            const responseData = response.data.data || response.data;
            const paginationData = response.data.pagination || {};

            const filteredMessages = responseData.filter(message => {
                try {
                    const msg = JSON.parse(message.text);
                    return (msg.body) ? true : false;
                } catch (e) {
                    return false;
                }
            });

            // Parse the JSON text and extract the body for display
            const processedMessages = filteredMessages.map(message => {
                try {
                    const parsedText = JSON.parse(message.text);
                    return {
                        ...message,
                        text: parsedText.body // fallback to original text if parsing fails
                    };
                } catch (e) {
                    return message;
                }
            });

            setMessages(processedMessages);
            setPagination(paginationData);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const handleReply = (message) => {
        navigate('/whatsapp/reply-chat', {
            state: {
                originalMessage: message,
                fromId: message.from_id,
                fromName: message.from_name
            }
        });
    };

    const handlePreviousPage = () => {
        if (pagination.hasPreviousPage) {
            setPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination.hasNextPage) {
            setPage(prev => prev + 1);
        }
    };

    return (
        <Sheet
            variant="outlined"
            sx={{
                maxWidth: 600,
                mx: 'auto',
                my: 4,
                py: 3,
                px: 2,
                borderRadius: 'md',
                boxShadow: 'md',
            }}
        >
            <Typography level="h4" component="h1" sx={{ mb: 2 }}>
                WhatsApp Chat
            </Typography>

            <Box
                sx={{
                    height: 400,
                    overflowY: 'auto',
                    bgcolor: 'background.level1',
                    p: 2,
                    borderRadius: 'md',
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column-reverse'
                }}
            >
                {messages.map((message) => (
                    <Box
                        key={message.id}
                        sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'background.level2',
                            borderRadius: 'lg',
                            maxWidth: '80%',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                bottom: 12,
                                left: -6,
                                width: 12,
                                height: 12,
                                bgcolor: 'background.level2',
                                transform: 'rotate(45deg)',
                                borderRadius: '2px'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 'md' }}>
                                {message.from_name}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                • {formatPhoneId(parseInt(message.from_id))}
                            </Typography>
                        </Box>
                        <Typography level="body-md" sx={{ my: 0.5 }}>
                            {message.text}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 1
                        }}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                {new Date(message.createdAt).toLocaleString()}
                            </Typography>
                            <Button
                                size="sm"
                                variant="soft"
                                color="primary"
                                onClick={() => handleReply(message)}
                            >
                                Reply
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    onClick={handlePreviousPage}
                    disabled={!pagination.hasPreviousPage}
                    variant="soft"
                    color="primary"
                >
                    Previous
                </Button>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Page {page} of {pagination.totalPages || 1} ({pagination.total || 0} total)
                </Typography>
                <Button
                    onClick={handleNextPage}
                    disabled={!pagination.hasNextPage}
                    variant="soft"
                    color="primary"
                >
                    Next
                </Button>
            </Box>
        </Sheet>
    );
};

export default Chat; 